// ============================================================
// caseAssignment.ts
// THE single save path for a case's assignment — used by BOTH the case
// quick-view modal and the case-list right-click "Assign to" dialog, so the
// two can never drift.
//
//   • Owner (fee earner) → PATCH /api/cases/:id/assignee (updateCaseAssigneeApi)
//     — unchanged endpoint + rules (admins assign anyone; non-admins may only
//     self-assign / unassign — backend-enforced).
//   • Audience (handling team / firm-wide) → PUT /api/cases/:id (updateCaseApi)
//     with audiencePayload(...) — ONLY the fields this user may change; the
//     backend's diff-gated updateCase is the real gate.
//
// Each call is skipped when it would be a no-op, so saving an unchanged dialog
// makes no request (and never trips the diff-gate).
// ============================================================
import { updateCaseApi, updateCaseAssigneeApi } from '../../api/casevoApi'

export interface CaseAssignmentState {
  id: string
  assignedToUserId: string | null
  firmWide?: boolean
  assignees?: { userId: string }[]
}
export interface CaseAssignmentResult {
  assignedToUserId: string | null
  firmWide: boolean
  assigneeIds: string[]
}

const sameSet = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id) => b.includes(id))

// Would this audience payload change what's stored on the case?
export const audiencePayloadChanges = (
  c: CaseAssignmentState,
  payload: { firmWide?: boolean; assigneeUserIds?: string[] },
): boolean => {
  const currentIds = (c.assignees ?? []).map((a) => a.userId)
  if (payload.firmWide !== undefined && payload.firmWide !== (c.firmWide ?? false)) return true
  if (payload.assigneeUserIds !== undefined && !sameSet(payload.assigneeUserIds, currentIds)) return true
  return false
}

export const saveCaseAssignment = async (
  c: CaseAssignmentState,
  next: {
    /** undefined = keep the current owner; null = unassign; string = user id. */
    ownerId: string | null | undefined
    /** From audiencePayload(audience, ids, canPeople, canFirm). */
    audience: { firmWide?: boolean; assigneeUserIds?: string[] }
  },
): Promise<CaseAssignmentResult> => {
  let assignedToUserId = c.assignedToUserId
  let firmWide = c.firmWide ?? false
  let assigneeIds = (c.assignees ?? []).map((a) => a.userId)

  if (next.ownerId !== undefined && next.ownerId !== c.assignedToUserId) {
    await updateCaseAssigneeApi(c.id, next.ownerId)
    assignedToUserId = next.ownerId
  }
  if (audiencePayloadChanges(c, next.audience)) {
    const saved = await updateCaseApi(c.id, next.audience)
    firmWide = saved.firmWide ?? false
    assigneeIds = (saved.assignees ?? []).map((a) => a.userId)
  }
  return { assignedToUserId, firmWide, assigneeIds }
}

// User-facing message for a failed assignment save (shared wording).
export const assignmentErrorMessage = (err: unknown): string => {
  if (err instanceof Error && /permission/i.test(err.message)) return err.message
  if (err instanceof Error && /403|yourself/i.test(err.message)) return 'You can only assign cases to yourself.'
  return 'Failed to save the assignment. Please try again.'
}
